/**
 * Materials API service
 * Handles all materials-related API operations
 */

import { httpClient } from '@/utils/httpClient';
import type {
    APIMaterial,
    APIMaterialRequest,
    DRFPaginatedResponse,
    MaterialCategory,
    MaterialUnit,
} from '@/types';

const buildQuery = (params: Record<string, unknown>): string => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) usp.append(k, String(v));
    });
    const s = usp.toString();
    return s ? `?${s}` : '';
};

export interface MaterialListParams {
    page?: number;
    page_size?: number;
    active?: boolean;
    category?: MaterialCategory;
    unit?: MaterialUnit;
}

export const materialsService = {
    async list(params?: MaterialListParams): Promise<DRFPaginatedResponse<APIMaterial>> {
        const qs = buildQuery((params ?? {}) as Record<string, unknown>);
        return httpClient.get<DRFPaginatedResponse<APIMaterial>>(`/materials/${qs}`);
    },

    async getById(id: number): Promise<APIMaterial> {
        return httpClient.get<APIMaterial>(`/materials/${id}/`);
    },

    async create(data: APIMaterialRequest): Promise<APIMaterial> {
        return httpClient.post<APIMaterial, APIMaterialRequest>('/materials/', data, {
            successTitle: 'Material creado',
            successMessage: 'El material se añadió al inventario.',
            errorTitle: 'Error en inventario',
        });
    },

    async update(id: number, data: APIMaterialRequest): Promise<APIMaterial> {
        return httpClient.put<APIMaterial, APIMaterialRequest>(`/materials/${id}/`, data, {
            successTitle: 'Material actualizado',
            successMessage: 'Los datos del material se guardaron.',
            errorTitle: 'Error en inventario',
        });
    },

    async patch(id: number, data: Partial<APIMaterialRequest>): Promise<APIMaterial> {
        return httpClient.patch<APIMaterial, Partial<APIMaterialRequest>>(`/materials/${id}/`, data, {
            successTitle: 'Material actualizado',
            successMessage: 'Se actualizaron los datos del material.',
            errorTitle: 'Error en inventario',
        });
    },

    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(`/materials/${id}/`, {
            successTitle: 'Material eliminado',
            successMessage: 'El material se eliminó del inventario.',
            errorTitle: 'Error en inventario',
        });
    },
};

export default materialsService;
